import React, { Children, Component } from 'react';
import { motion } from 'framer-motion';
import { TrendingUpIcon, UsersIcon, ShoppingCartIcon, BuildingIcon, ZapIcon, CheckCircleIcon } from 'lucide-react';
import { Badge } from './ui/Badge';
import { Card, CardBody } from './ui/Card';
const metrics = [{
  icon: TrendingUpIcon,
  value: '40%',
  label: 'Bounce Rate Reduction',
  context: 'Ashley Global Retail',
  description: 'Redesigned digital experience architecture reducing bounce rates through improved UX hierarchy and personalization.'
}, {
  icon: TrendingUpIcon,
  value: '15%',
  label: 'Revenue Increase',
  context: 'Enterprise CX Transformation',
  description: 'Drove measurable revenue growth through omni-channel brand alignment and customer journey optimization.'
}, {
  icon: UsersIcon,
  value: '60%',
  label: 'Engagement Growth',
  context: 'Multi-Division Rebrand',
  description: 'Achieved significant engagement lift across all divisions through unified brand architecture and governance.'
}, {
  icon: BuildingIcon,
  value: '15+',
  label: 'White House Components',
  context: 'Federal Modernization',
  description: 'Partnered with cross-functional teams to modernize 15+ White House digital components, ensuring compliance with federal accessibility mandates.'
}, {
  icon: ShoppingCartIcon,
  value: '27%',
  label: 'Cart Abandonment Reduction',
  context: 'Retail CX Optimization',
  description: 'Reduced cart abandonment through streamlined checkout UX, trust signal design, and A/B-tested flows.'
}];
const tools = ['Figma', 'Adobe CC', 'Salesforce', 'IBM Carbon', 'Material UI', 'Bootstrap UI', 'Jira', 'Azure DevOps', 'Asana', 'Wrike', 'Sketch', 'AI/GAI Integration'];
const complianceBadges = [{
  label: 'Section 508',
  icon: '✓'
}, {
  label: 'WCAG 2.1 AA',
  icon: '✓'
}, {
  label: 'ADA Compliant',
  icon: '✓'
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function RecruiterMode() {
  return <div data-id="element-515">
      {/* Section header */}
      <div className="mb-12" data-id="element-516">
        <span className="inline-flex items-center gap-2 text-[#B08D57] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-517">
          <span className="w-6 h-px bg-[#B08D57]" aria-hidden="true" data-id="element-518" />
          Recruiter View — At a Glance
        </span>
        <h2 className="font-heading font-bold text-[#1C1F23] text-3xl lg:text-4xl mb-4" data-id="element-519">
          Measurable Enterprise Impact
        </h2>
        <p className="font-body text-[#4A5A66] max-w-2xl" data-id="element-520">
          Quantified outcomes across federal, retail, and enterprise
          environments — delivered through design leadership, systems thinking,
          and cross-functional execution.
        </p>
      </div>

      {/* Metrics grid */}
      <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-50px'
    }} role="list" aria-label="Key performance metrics" data-id="element-521">
        {metrics.map(metric => {
        const Icon = metric.icon;
        return <motion.div key={metric.value + metric.label} variants={itemVariants} role="listitem" data-id="element-522">
              <Card elevated bordered className="h-full" data-id="element-523">
                <CardBody className="flex flex-col gap-3" data-id="element-524">
                  <div className="flex items-start justify-between" data-id="element-525">
                    <div className="w-10 h-10 rounded-lg bg-[#B08D57]/10 flex items-center justify-center flex-shrink-0" aria-hidden="true" data-id="element-526">
                      <Icon className="w-5 h-5 text-[#B08D57]" data-id="element-527" />
                    </div>
                    <Badge variant="gold" data-id="element-528">{metric.context}</Badge>
                  </div>
                  <div data-id="element-529">
                    <div className="font-heading font-bold text-4xl text-[#2F3A45] leading-none mb-1" data-id="element-530">
                      {metric.value}
                    </div>
                    <div className="font-body font-semibold text-[#1C1F23] text-sm mb-2" data-id="element-531">
                      {metric.label}
                    </div>
                    <p className="font-body text-[#4A5A66] text-sm leading-relaxed" data-id="element-532">
                      {metric.description}
                    </p>
                  </div>
                </CardBody>
              </Card>
            </motion.div>;
      })}
      </motion.div>

      {/* Tool stack & compliance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-533">
        {/* Tool stack */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} data-id="element-534">
          <Card bordered data-id="element-535">
            <CardBody data-id="element-536">
              <div className="flex items-center gap-2 mb-4" data-id="element-537">
                <ZapIcon className="w-4 h-4 text-[#B08D57]" aria-hidden="true" data-id="element-538" />
                <h3 className="font-heading font-semibold text-[#1C1F23] text-base" data-id="element-539">
                  Tool Stack & Platforms
                </h3>
              </div>
              <div className="flex flex-wrap gap-2" role="list" aria-label="Tools and platforms" data-id="element-540">
                {tools.map(tool => <Badge key={tool} variant="slate" role="listitem" data-id="element-541">
                    {tool}
                  </Badge>)}
              </div>
            </CardBody>
          </Card>
        </motion.div>

        {/* Compliance */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} data-id="element-542">
          <Card bordered data-id="element-543">
            <CardBody data-id="element-544">
              <div className="flex items-center gap-2 mb-4" data-id="element-545">
                <CheckCircleIcon className="w-4 h-4 text-emerald-600" aria-hidden="true" data-id="element-546" />
                <h3 className="font-heading font-semibold text-[#1C1F23] text-base" data-id="element-547">
                  Accessibility & Compliance
                </h3>
              </div>
              <div className="flex flex-wrap gap-2 mb-4" role="list" aria-label="Compliance certifications" data-id="element-548">
                {complianceBadges.map(badge => <Badge key={badge.label} variant="compliance" role="listitem" data-id="element-549">
                    {badge.icon} {badge.label}
                  </Badge>)}
              </div>
              <p className="font-body text-[#4A5A66] text-sm leading-relaxed" data-id="element-550">
                Deep expertise in federal accessibility standards, inclusive
                design practices, and enterprise-scale governance frameworks
                that ensure equitable digital experiences.
              </p>
            </CardBody>
          </Card>
        </motion.div>
      </div>
    </div>;
}